/**
 * Processing Journal tab: read-only viewer for processing pipeline runs and their entries.
 *
 * Left panel: list of recent ProcessingRuns with filter bar (search, date range, status, trigger type).
 * Right panel: when a run is selected, shows journal entries as a timeline.
 */
import { useState, useMemo, useEffect } from "react";
import {
    ChevronDown,
    ChevronRight,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    RefreshCw05,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { PdfViewer } from "@/components/application/pdf-viewer/pdf-viewer";
import { useDocument, useDocumentBlobUrl } from "@/hooks/api/use-documents";
import type { ReviewPolygon } from "@/types/document-review";


// ============================================================================
// Types
// ============================================================================

interface ProcessingRun {
    run_id: string;
    company_id: string;
    container_id: string | null;
    container_ref: string | null;
    trigger_type: string;
    trigger_id: string | null;
    trigger_description: string | null;
    parent_run_id: string | null;
    status: string;
    started_at: string;
    completed_at: string | null;
    duration_ms: number | null;
    steps_total: number;
    steps_succeeded: number;
    steps_failed: number;
    steps_skipped: number;
    error_summary: string | null;
}

interface JournalEntry {
    entry_id: string;
    run_id: string;
    sequence_number: number;
    step_type: string;
    step_name: string;
    container_id: string | null;
    document_id: string | null;
    email_message_id: string | null;
    input_snapshot: Record<string, unknown> | null;
    output_snapshot: Record<string, unknown> | null;
    decision_summary: string | null;
    status: string;
    error_message: string | null;
    warning_message: string | null;
    started_at: string;
    completed_at: string | null;
    duration_ms: number | null;
    ai_response_text: string | null;
    step_metadata: Record<string, unknown> | null;
    display_labels: { container?: string; document?: string; email?: string } | null;
}

interface RunDetailResponse {
    run: ProcessingRun;
    entries: JournalEntry[];
    total: number;
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleString("it-IT");
}

function formatDuration(ms: number | null): string {
    if (ms === null) return "";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

type BadgeColor = "brand" | "success" | "error" | "warning" | "purple" | "gray" | "blue" | "orange";

function getTriggerColor(triggerType: string): BadgeColor {
    switch (triggerType) {
        case "email_sync": return "brand";
        case "document_extraction": return "success";
        case "cross_check": return "warning";
        case "debug_reprocess": return "purple";
        case "tracking_sync": return "blue";
        case "risk_assessment": return "orange";
        default: return "gray";
    }
}

function getStatusColor(status: string): BadgeColor {
    switch (status) {
        case "completed": return "success";
        case "failed": return "error";
        case "running": return "brand";
        case "partial": return "warning";
        default: return "gray";
    }
}

function getEntryDotColor(status: string): string {
    switch (status) {
        case "success": return "bg-success-500";
        case "failed": return "bg-error-500";
        case "skipped": return "bg-warning-500";
        case "running": return "bg-brand-500 animate-pulse";
        default: return "bg-gray-400";
    }
}

// ============================================================================
// Sub-components
// ============================================================================

function RunCard({
    run,
    isSelected,
    onSelect,
}: {
    run: ProcessingRun;
    isSelected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={[
                "w-full text-left rounded-lg border p-3 space-y-2 transition-colors",
                isSelected
                    ? "border-brand-300 bg-brand-primary_alt"
                    : "border-secondary bg-primary hover:bg-secondary",
            ].join(" ")}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <Badge color={getTriggerColor(run.trigger_type)} size="sm">
                        {run.trigger_type}
                    </Badge>
                    <Badge color={getStatusColor(run.status)} size="sm">
                        {run.status === "running" && (
                            <RefreshCw05 className="h-3 w-3 animate-spin" />
                        )}
                        {run.status}
                    </Badge>
                </div>
                <span className="text-xs text-tertiary shrink-0 whitespace-nowrap">
                    {formatRelativeTime(run.started_at)}
                </span>
            </div>

            {run.trigger_description && (
                <p className="text-xs text-secondary truncate">
                    {run.trigger_description}
                </p>
            )}
            {run.container_ref && (
                <p className="text-xs text-tertiary">
                    Container: <span className="font-medium text-secondary">{run.container_ref}</span>
                </p>
            )}

            <div className="flex items-center gap-3 text-xs text-tertiary">
                <span>{run.steps_total} steps</span>
                {run.steps_failed > 0 && (
                    <span className="text-error-primary font-medium">
                        {run.steps_failed} failed
                    </span>
                )}
                {run.duration_ms !== null && (
                    <span>{formatDuration(run.duration_ms)}</span>
                )}
            </div>

            {run.error_summary && (
                <p className="text-xs text-error-primary truncate">{run.error_summary}</p>
            )}
        </button>
    );
}

function JsonBlock({ label, data }: { label: string; data: Record<string, unknown> | null }) {
    const [expanded, setExpanded] = useState(false);
    if (!data) return null;

    return (
        <div className="space-y-1">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center gap-1 text-xs text-tertiary hover:text-secondary"
            >
                {expanded ? (
                    <ChevronDown className="h-3 w-3" />
                ) : (
                    <ChevronRight className="h-3 w-3" />
                )}
                {label}
            </button>
            {expanded && (
                <pre className="text-xs font-mono bg-gray-950 text-gray-300 rounded-lg p-3 overflow-auto max-h-60">
                    {JSON.stringify(data, null, 2)}
                </pre>
            )}
        </div>
    );
}

function AiReasoningBlock({ text }: { text: string | null }) {
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;

    return (
        <div className="space-y-1">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center gap-1 text-xs text-tertiary hover:text-secondary"
            >
                {expanded ? (
                    <ChevronDown className="h-3 w-3" />
                ) : (
                    <ChevronRight className="h-3 w-3" />
                )}
                AI Reasoning
            </button>
            {expanded && (
                <pre className="text-xs font-mono bg-gray-950 text-gray-300 rounded-lg p-3 overflow-auto max-h-60 whitespace-pre-wrap">
                    {text}
                </pre>
            )}
        </div>
    );
}

const EMPTY_REVIEW_POLYGONS: ReviewPolygon[] = [];

const PDF_MIME = new Set(["application/pdf", "application/x-pdf"]);
const IMAGE_MIME_PREFIX = "image/";

function isPdfMime(mime: string | null | undefined, fileName?: string | null): boolean {
    if (mime && PDF_MIME.has(mime.toLowerCase())) return true;
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return ext === "pdf";
}

function isImageMime(mime: string | null | undefined, fileName?: string | null): boolean {
    if (mime?.toLowerCase().startsWith(IMAGE_MIME_PREFIX)) return true;
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return !!ext && ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "tiff", "tif"].includes(ext);
}

function JournalDocumentPreviewSlideout({
    documentId,
    fileLabel,
    isOpen,
    onOpenChange,
}: {
    documentId: string | null;
    fileLabel: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const { data: docMeta, isLoading: metaLoading, error: metaError } = useDocument(documentId ?? "");
    const {
        data: fileUrl,
        isLoading: blobLoading,
        error: blobError,
    } = useDocumentBlobUrl(documentId ?? undefined);

    const mime = docMeta?.file_mime_type ?? "";
    const showPdf = isPdfMime(mime, docMeta?.file_name);
    const showImage = !showPdf && isImageMime(mime, docMeta?.file_name);
    const loading = metaLoading || blobLoading;
    const err = metaError || blobError;
    const title = fileLabel || docMeta?.file_name || documentId || "Document";

    return (
        <SlideoutMenu
            isOpen={isOpen}
            onOpenChange={onOpenChange}
            className="ml-auto w-screen md:w-[min(90vw,560px)] max-w-none pl-0 md:pl-0 z-[1000]"
            dialogClassName="bg-primary_alt ring-secondary_alt shadow-xl w-full pl-0 flex flex-col h-full max-h-dvh p-0 gap-0"
        >
            <SlideoutMenu.Content className="flex flex-col h-full min-h-0 p-0 gap-0 overflow-hidden">
                <SlideoutMenu.Header onClose={() => onOpenChange(false)} className="shrink-0 border-b border-secondary">
                    <h3 className="text-lg font-medium text-primary pr-10 truncate" title={title}>
                        {title}
                    </h3>
                    {documentId && (
                        <p className="text-xs text-tertiary font-mono truncate mt-0.5">{documentId}</p>
                    )}
                </SlideoutMenu.Header>
                <div className="flex-1 min-h-0 overflow-auto px-4 py-3 md:px-6">
                    {loading && (
                        <div className="flex items-center justify-center py-16 text-tertiary text-sm">
                            <RefreshCw05 className="h-4 w-4 animate-spin mr-2" />
                            Loading preview…
                        </div>
                    )}
                    {!loading && err && (
                        <div className="flex items-start gap-2 text-sm text-error-primary py-4">
                            <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>{err instanceof Error ? err.message : "Could not load document"}</span>
                        </div>
                    )}
                    {!loading && !err && fileUrl && showPdf && (
                        <div className="h-[min(70vh,600px)]">
                            <PdfViewer
                                key={fileUrl}
                                fileUrl={fileUrl}
                                polygons={EMPTY_REVIEW_POLYGONS}
                                hideAddRectangle
                                showZoomControls
                            />
                        </div>
                    )}
                    {!loading && !err && fileUrl && showImage && (
                        <div className="flex justify-center items-start bg-secondary rounded-lg p-2">
                            <img
                                src={fileUrl}
                                alt={title}
                                className="max-w-full max-h-[min(70vh,600px)] object-contain"
                            />
                        </div>
                    )}
                    {!loading && !err && fileUrl && !showPdf && !showImage && (
                        <div className="space-y-3 text-sm text-secondary">
                            <p>Preview is not available for this file type.</p>
                            <a
                                href={fileUrl}
                                download={docMeta?.file_name ?? "document"}
                                className="inline-flex font-medium text-brand-secondary hover:underline"
                            >
                                Open / download file
                            </a>
                        </div>
                    )}
                </div>
            </SlideoutMenu.Content>
        </SlideoutMenu>
    );
}

function EntryCard({
    entry,
    onOpenDocumentPreview,
}: {
    entry: JournalEntry;
    onOpenDocumentPreview?: (documentId: string, label: string) => void;
}) {
    return (
        <div className="flex gap-3">
            {/* Timeline dot */}
            <div className="flex flex-col items-center mt-1 shrink-0">
                <div className={`h-2.5 w-2.5 rounded-full ${getEntryDotColor(entry.status)}`} />
                <div className="w-px flex-1 bg-secondary mt-1" />
            </div>

            {/* Entry content */}
            <div className="pb-4 space-y-2 min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                        {entry.document_id && onOpenDocumentPreview ? (
                            <button
                                type="button"
                                className="text-sm font-medium text-brand-secondary hover:underline text-left truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                                onClick={() =>
                                    onOpenDocumentPreview(
                                        entry.document_id!,
                                        entry.display_labels?.document ?? entry.step_name,
                                    )
                                }
                                aria-label={`Apri anteprima documento: ${entry.display_labels?.document ?? entry.step_name}`}
                            >
                                {entry.step_name}
                            </button>
                        ) : (
                            <span className="text-sm font-medium text-primary truncate">
                                {entry.step_name}
                            </span>
                        )}
                        <Badge color="gray" size="sm">
                            {entry.step_type}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {(entry.output_snapshot?.model_used as string) && (
                            <Badge color="purple" size="sm">
                                {String(entry.output_snapshot!.model_used).replace("anthropic/", "")}
                            </Badge>
                        )}
                        {entry.duration_ms !== null && (
                            <span className="text-xs text-tertiary">
                                {formatDuration(entry.duration_ms)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Human-readable reference labels */}
                {entry.display_labels && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {entry.display_labels.container && (
                            <span className="text-xs text-tertiary">
                                <span className="text-quaternary">Container:</span> {entry.display_labels.container}
                            </span>
                        )}
                        {entry.display_labels.document && (
                            entry.document_id && onOpenDocumentPreview ? (
                                <button
                                    type="button"
                                    className="text-xs text-brand-secondary hover:underline text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                                    onClick={() =>
                                        onOpenDocumentPreview(
                                            entry.document_id!,
                                            entry.display_labels!.document!,
                                        )
                                    }
                                    aria-label={`Apri anteprima documento: ${entry.display_labels.document}`}
                                >
                                    <span className="text-quaternary">Doc:</span> {entry.display_labels.document}
                                </button>
                            ) : (
                                <span className="text-xs text-tertiary">
                                    <span className="text-quaternary">Doc:</span> {entry.display_labels.document}
                                </span>
                            )
                        )}
                        {entry.display_labels.email && (
                            <span className="text-xs text-tertiary truncate max-w-xs">
                                <span className="text-quaternary">Email:</span> {entry.display_labels.email}
                            </span>
                        )}
                    </div>
                )}

                {entry.decision_summary && (
                    <p className="text-xs text-secondary">{entry.decision_summary}</p>
                )}

                {entry.error_message && (
                    <div className="flex items-start gap-1.5 text-xs text-error-primary">
                        <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>{entry.error_message}</span>
                    </div>
                )}

                {entry.warning_message && (
                    <div className="flex items-start gap-1.5 text-xs text-warning-primary">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>{entry.warning_message}</span>
                    </div>
                )}

                <div className="space-y-1.5">
                    <JsonBlock label="Input" data={entry.input_snapshot} />
                    <JsonBlock label="Output" data={entry.output_snapshot} />
                    <AiReasoningBlock text={entry.ai_response_text} />
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Main tab component
// ============================================================================

interface ProcessingJournalTabProps {
    /** Search text from parent's shared search bar */
    searchText?: string;
    /** Date from filter from parent */
    dateFrom?: string;
    /** Date to filter from parent */
    dateTo?: string;
    /** Run status filter (completed, failed, etc.) */
    runStatus?: string | null;
    /** Trigger type filter (email_sync, cross_check, etc.) */
    triggerType?: string | null;
    /** Callbacks for filter state management */
    onRunStatusChange?: (status: string | null) => void;
    onTriggerTypeChange?: (trigger: string | null) => void;
    /** Selected run ID (managed by parent for URL persistence) */
    selectedRunId?: string | null;
    onSelectedRunIdChange?: (id: string | null) => void;
    /** Callback to clear the parent's shared search/filters */
    onClearSearch?: () => void;
}

export function ProcessingJournalTab({
    searchText = "",
    dateFrom,
    dateTo,
    runStatus,
    triggerType,
    selectedRunId: externalSelectedRunId,
    onSelectedRunIdChange,
    onClearSearch,
}: ProcessingJournalTabProps) {
    const queryClient = useQueryClient();
    // Use parent-managed selectedRunId if provided, else local state
    const [localSelectedRunId, setLocalSelectedRunId] = useState<string | null>(null);
    const selectedRunId = externalSelectedRunId ?? localSelectedRunId;
    const setSelectedRunId = onSelectedRunIdChange ?? setLocalSelectedRunId;
    const [offset, setOffset] = useState(0);
    const [documentPreview, setDocumentPreview] = useState<{ id: string; label: string } | null>(null);

    const LIMIT = 20;

    // Reset offset when parent filters change
    useEffect(() => {
        setOffset(0);
    }, [searchText, dateFrom, dateTo, runStatus, triggerType]);

    // Build query params directly from parent props (no local mirror — avoids race on Clear)
    const queryParams = useMemo(() => {
        const params: Record<string, string | number> = {
            limit: LIMIT,
            offset,
        };
        if (searchText.trim()) params.search = searchText.trim();
        if (dateFrom) params.from_date = new Date(dateFrom).toISOString();
        if (dateTo) {
            const endOfDay = new Date(dateTo);
            endOfDay.setHours(23, 59, 59, 999);
            params.to_date = endOfDay.toISOString();
        }
        if (runStatus) params.status = runStatus;
        if (triggerType) params.trigger_type = triggerType;
        return params;
    }, [searchText, dateFrom, dateTo, runStatus, triggerType, offset]);

    // Fetch runs list
    const { data: runsList = [], isFetching: isLoadingRuns } = useQuery({
        queryKey: ["admin", "journal", "runs", queryParams],
        queryFn: () =>
            apiGet<ProcessingRun[]>("/processing-runs", { params: queryParams }),
    });

    // Selected run detail
    const { data: runDetail, isFetching: isLoadingDetail } = useQuery({
        queryKey: ["admin", "journal", "run", selectedRunId],
        queryFn: () =>
            apiGet<RunDetailResponse>(`/processing-runs/${selectedRunId}`, {
                params: { include_reasoning: "true" },
            }),
        enabled: !!selectedRunId,
    });

    const isLoading = isLoadingRuns;
    const hasMore = runsList.length >= LIMIT;

    return (
        <div className="space-y-4">
            <div className="flex gap-4 items-start h-[calc(100vh-280px)] min-h-[500px]">
                {/* Left panel — runs list with filters */}
                <div className="w-80 shrink-0 flex flex-col gap-2 h-full">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-primary">
                            Runs
                            {runsList.length > 0 && (
                                <span className="ml-1 text-tertiary font-normal">({runsList.length})</span>
                            )}
                        </span>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                className="p-1 rounded text-tertiary hover:text-primary hover:bg-secondary transition-colors"
                                title="Get Latest Runs"
                                onClick={() => queryClient.invalidateQueries({ queryKey: ["admin", "journal", "runs"] })}
                            >
                                <RefreshCw05 className={`h-3.5 w-3.5 ${isLoadingRuns ? "animate-spin" : ""}`} />
                            </button>
                            <button
                                type="button"
                                className="px-2 py-0.5 text-xs font-medium rounded border border-error-primary text-error-primary hover:bg-error-primary hover:text-white transition-colors"
                                onClick={() => {
                                    setSelectedRunId(null);
                                    setOffset(0);
                                    onClearSearch?.();
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Filters are shared from the parent Debug page */}

                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {isLoading && runsList.length === 0 && (
                            <div className="flex items-center justify-center py-12 text-tertiary text-sm">
                                <RefreshCw05 className="h-4 w-4 animate-spin mr-2" />
                                Loading...
                            </div>
                        )}

                        {!isLoading && runsList.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <Clock className="h-8 w-8 text-tertiary mb-2" />
                                <p className="text-sm text-tertiary">
                                    {!!(searchText || dateFrom || dateTo || runStatus || triggerType) ? "No runs match your filters." : "No processing runs found."}
                                </p>
                                {!!(searchText || dateFrom || dateTo || runStatus || triggerType) && onClearSearch && (
                                    <button
                                        type="button"
                                        onClick={onClearSearch}
                                        className="mt-2 text-xs text-brand-600 hover:text-brand-700 font-medium"
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        )}

                        {runsList.map((run) => (
                            <RunCard
                                key={run.run_id}
                                run={run}
                                isSelected={selectedRunId === run.run_id}
                                onSelect={() => setSelectedRunId(run.run_id)}
                            />
                        ))}

                        {hasMore && (
                            <Button
                                color="secondary"
                                size="sm"
                                className="w-full"
                                onPress={() => setOffset((prev) => prev + LIMIT)}
                                isDisabled={isLoading}
                            >
                                Load more
                            </Button>
                        )}
                    </div>
                </div>

                {/* Right panel — run detail */}
                <div className="flex-1 h-full overflow-y-auto rounded-lg border border-secondary bg-primary">
                    {!selectedRunId && (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8">
                            <Clock className="h-10 w-10 text-tertiary mb-3" />
                            <p className="text-sm font-medium text-secondary">Select a run</p>
                            <p className="text-xs text-tertiary mt-1">
                                Click a processing run on the left to inspect its timeline.
                            </p>
                        </div>
                    )}

                    {selectedRunId && isLoadingDetail && (
                        <div className="flex items-center justify-center h-full text-tertiary text-sm">
                            <RefreshCw05 className="h-4 w-4 animate-spin mr-2" />
                            Loading run details...
                        </div>
                    )}

                    {selectedRunId && runDetail && !isLoadingDetail && (
                        <div className="p-4 space-y-4">
                            {/* Run header */}
                            <div className="space-y-3 pb-4 border-b border-secondary">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="space-y-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Badge color={getTriggerColor(runDetail.run.trigger_type)} size="sm">
                                                {runDetail.run.trigger_type}
                                            </Badge>
                                            <Badge color={getStatusColor(runDetail.run.status)} size="sm">
                                                {runDetail.run.status === "running" && (
                                                    <RefreshCw05 className="h-3 w-3 animate-spin" />
                                                )}
                                                {runDetail.run.status}
                                            </Badge>
                                        </div>
                                        {runDetail.run.trigger_description && (
                                            <p className="text-sm text-secondary">
                                                {runDetail.run.trigger_description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-xs text-tertiary shrink-0 text-right space-y-0.5">
                                        <p>{formatRelativeTime(runDetail.run.started_at)}</p>
                                        {runDetail.run.duration_ms !== null && (
                                            <p>{formatDuration(runDetail.run.duration_ms)}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Stats row */}
                                <div className="flex items-center gap-4 text-xs">
                                    <div className="flex items-center gap-1 text-secondary">
                                        <CheckCircle className="h-3.5 w-3.5 text-success-500" />
                                        <span>{runDetail.run.steps_succeeded} ok</span>
                                    </div>
                                    {runDetail.run.steps_failed > 0 && (
                                        <div className="flex items-center gap-1 text-error-primary">
                                            <XCircle className="h-3.5 w-3.5" />
                                            <span>{runDetail.run.steps_failed} failed</span>
                                        </div>
                                    )}
                                    {runDetail.run.steps_skipped > 0 && (
                                        <div className="flex items-center gap-1 text-tertiary">
                                            <AlertTriangle className="h-3.5 w-3.5" />
                                            <span>{runDetail.run.steps_skipped} skipped</span>
                                        </div>
                                    )}
                                    <span className="text-tertiary">
                                        {runDetail.run.steps_total} total
                                    </span>
                                </div>

                                {runDetail.run.error_summary && (
                                    <div className="flex items-start gap-1.5 text-xs text-error-primary">
                                        <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                        <span>{runDetail.run.error_summary}</span>
                                    </div>
                                )}

                                <p className="text-xs text-tertiary font-mono">
                                    run: {runDetail.run.run_id}
                                </p>
                            </div>

                            {/* Entries timeline */}
                            {runDetail.entries.length === 0 ? (
                                <p className="text-sm text-tertiary text-center py-6">
                                    No entries recorded for this run.
                                </p>
                            ) : (
                                <div className="space-y-0">
                                    {runDetail.entries
                                        .slice()
                                        .sort((a, b) => a.sequence_number - b.sequence_number)
                                        .map((entry) => (
                                            <EntryCard
                                                key={entry.entry_id}
                                                entry={entry}
                                                onOpenDocumentPreview={(id, label) =>
                                                    setDocumentPreview({ id, label })
                                                }
                                            />
                                        ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <JournalDocumentPreviewSlideout
                documentId={documentPreview?.id ?? null}
                fileLabel={documentPreview?.label ?? ""}
                isOpen={!!documentPreview}
                onOpenChange={(open) => {
                    if (!open) {
                        setDocumentPreview(null);
                    }
                }}
            />
        </div>
    );
}
